import { DropdownIcon } from 'assets/icons';
import styles from './CompositionTableSettings.module.css';
import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { ClearIcon, TrashCanIcon } from 'features/compositions/assets/icons/icons';
import ThemeContext from 'config/context/themeContext';
import { RequireAuth } from 'features/permissions';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useDropComposition } from 'features/compositions/services/useDropCompositionService';
import { useClearComposition } from 'features/compositions/services/useClearCompositionService';

const CompositionTableSettings = () => {
    const wrapperRef = useRef(null);
    const { dark } = useContext(ThemeContext);
    const [opened, setOpened] = useState(false);
    const [mode, setMode] = useState('');
    const { tempPrivateKey, selectedComposition } = useCompositionsContext();
    const { dropComposition, response: dropResponse, loading: dropLoading, error: dropError, resetServerStatus: dropResetServerStatus } = useDropComposition();
    const { clearComposition, response: clearResponse, loading: clearLoading, error: clearError, resetServerStatus: clearResetServerStatus } = useClearComposition();
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if loading is in progress
            if (wrapperRef.current && !wrapperRef.current.contains(event.target) && !dropLoading && !clearLoading) {
                setOpened(false);
                setMode('');
                dropResetServerStatus();
                clearResetServerStatus();
            }
        };
        
        if (opened) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [opened, dropLoading, clearLoading, dropResetServerStatus, clearResetServerStatus]);

    // Handle composition deletion
    const handleDeleteComposition = useCallback(async () => {
        try {
            await dropComposition({
                composition: selectedComposition?.name,
                private_key: tempPrivateKey
            });
        } catch (error) {
            console.error('Error deleting composition:', error);
        }
    }, [dropComposition, selectedComposition?.name, tempPrivateKey]);

    // Handle composition clearing
    const handleClearComposition = useCallback(async () => {
        try {
            await clearComposition({
                composition: selectedComposition?.name,
                private_key: tempPrivateKey
            });
        } catch (error) {
            console.error('Error clearing composition:', error);
        }
    }, [clearComposition, selectedComposition?.name, tempPrivateKey]);

    // Auto-close settings when deletion is successful
    useEffect(() => {
        if (dropResponse?.status) {
            setMode('');
            setOpened(false);
            dropResetServerStatus();
        }
    }, [dropResponse, dropResetServerStatus]);

    // Auto-close settings when clearing is successful
    useEffect(() => {
        if (clearResponse?.status) {
            setMode('');
            setOpened(false);
            clearResetServerStatus();
        }
    }, [clearResponse, clearResetServerStatus]);

    const fallbackComponent = (
        <div className={`${styles.composition_table_settings} ${dark ? styles.dark : ''}`}>
            <button className={styles.composition_settings_btn}>
                <div>{selectedComposition?.name}</div> <span>({selectedComposition?.scope_count})</span>
            </button>
        </div>
    );

    return (
        <RequireAuth
            permission={'active'}
            hasTempKey={!!tempPrivateKey}
            fallback={fallbackComponent}
        >
            <div
                ref={wrapperRef}
                className={`${styles.composition_table_settings} ${dark ? styles.dark : ''}`}
            >
                <button 
                    onClick={() => { setOpened(!opened); setMode(''); }} 
                    className={styles.composition_settings_btn}
                    disabled={dropLoading || clearLoading}
                >
                    <div>{selectedComposition?.name}</div> <span>({selectedComposition?.scope_count})</span> <DropdownIcon color={dark ? '#E0E0E0' : ''} />
                </button>
                {opened &&
                    <div className={styles.composition_table_settings_menu}>
                        <div className={styles.options}>
                            <button 
                                onClick={() => setMode('clear')} 
                                className={styles.standard_btn}
                                disabled={dropLoading || clearLoading}
                            >
                                Clear all table data <ClearIcon color={dark ? '#CCCCCC' : ''} />
                            </button>
                            <button 
                                onClick={() => setMode('delete')} 
                                className={styles.danger_btn}
                                disabled={dropLoading || clearLoading}
                            >
                                Delete composition <TrashCanIcon />
                            </button>
                        </div>
                        {mode === 'clear' &&
                            <div className={styles.decisions}>
                                <button 
                                    onClick={() => { setMode(''); setOpened(false); clearResetServerStatus(); }}
                                    disabled={dropLoading || clearLoading}
                                >
                                    Decline
                                </button>
                                <button 
                                    onClick={handleClearComposition}
                                    disabled={dropLoading || clearLoading}
                                    className={clearLoading ? styles.loading : ''}
                                >
                                    {clearLoading ? 'Please wait...' : 'Clear'}
                                </button>
                            </div>
                        }
                        {mode === 'delete' &&
                            <div className={styles.decisions}>
                                <button 
                                    onClick={() => { setMode(''); setOpened(false); dropResetServerStatus(); }}
                                    disabled={dropLoading || clearLoading}
                                >
                                    Decline
                                </button>
                                <button 
                                    onClick={handleDeleteComposition}
                                    disabled={dropLoading || clearLoading}
                                    className={dropLoading ? styles.loading : ''}
                                >
                                    {dropLoading ? 'Please wait...' : 'Delete'}
                                </button>
                            </div>
                        }
                        {dropError && (
                            <div className={styles.error_message}>
                                Error: {dropError}
                            </div>
                        )}
                        {clearError && (
                            <div className={styles.error_message}>
                                Error: {clearError}
                            </div>
                        )}
                    </div>
                }
            </div>
        </RequireAuth>
    )
}

export default CompositionTableSettings;